import React, { useMemo, useState } from 'react';
import {
    useReactTable,
    getCoreRowModel,
    getSortedRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    flexRender
} from '@tanstack/react-table';
import { Pencil, Copy, Trash2, Eye, FileText, ChevronUp, ChevronDown } from 'lucide-react';
import ModernPagination from '../../ui/ModernPagination';

const ContractsTable = ({ data = [], isLoading, onEdit, onDelete, onDuplicate, onPreview }) => {
    const [sorting, setSorting] = useState([]);
    const [globalFilter, setGlobalFilter] = useState('');

    const columns = useMemo(() => [
        {
            id: 'title',
            accessorKey: 'title',
            header: 'Titel',
            cell: ({ row }) => (
                <span style={{ fontWeight: 500, color: 'var(--text-primary, #2c3e50)' }}>
                    {row.original.title}
                </span>
            ),
        },
        {
            id: 'type',
            accessorKey: 'art',
            header: 'Kategorie',
            cell: ({ row }) => {
                const type = parseInt(row.original.art);
                const isWedding = type === 2;
                return (
                    <span className={`badge ${isWedding ? 'badge-type-wedding' : 'badge-type-jobs'}`}>
                        {row.original.type_label}
                    </span>
                );
            },
        },
        {
            id: 'actions',
            header: '',
            cell: ({ row }) => {
                const actionBtnStyle = {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '32px',
                    height: '32px',
                    border: 'none',
                    borderRadius: '6px',
                    background: 'transparent',
                    color: 'var(--text-secondary, #64748b)',
                    cursor: 'pointer',
                    padding: 0,
                    outline: 'none'
                };

                return (
                    <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'flex-end' }}>
                        <button
                            style={actionBtnStyle}
                            title="Vorschau"
                            onClick={() => onPreview && onPreview(row.original)}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'rgba(59, 130, 246, 0.1)';
                                e.currentTarget.style.color = '#3b82f6';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'transparent';
                                e.currentTarget.style.color = 'var(--text-secondary, #64748b)';
                            }}
                        >
                            <Eye size={16} />
                        </button>
                        <button
                            style={actionBtnStyle}
                            title="Bearbeiten"
                            onClick={() => onEdit(row.original)}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'var(--bg-secondary, #f5f7f9)';
                                e.currentTarget.style.color = 'var(--accent-slate, #5b7c8d)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'transparent';
                                e.currentTarget.style.color = 'var(--text-secondary, #64748b)';
                            }}
                        >
                            <Pencil size={16} />
                        </button>
                        <button
                            style={actionBtnStyle}
                            title="Duplizieren"
                            onClick={() => onDuplicate(row.original)}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'rgba(127, 179, 161, 0.1)';
                                e.currentTarget.style.color = 'var(--accent-mint, #7fb3a1)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'transparent';
                                e.currentTarget.style.color = 'var(--text-secondary, #64748b)';
                            }}
                        >
                            <Copy size={16} />
                        </button>
                        <button
                            style={actionBtnStyle}
                            title="Löschen"
                            onClick={() => onDelete(row.original)}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
                                e.currentTarget.style.color = '#ef4444';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'transparent';
                                e.currentTarget.style.color = 'var(--text-secondary, #64748b)';
                            }}
                        >
                            <Trash2 size={16} />
                        </button>
                    </div>
                );
            },
        },
    ], [onEdit, onDelete, onDuplicate, onPreview]);

    const table = useReactTable({
        data,
        columns,
        state: {
            sorting,
            globalFilter,
        },
        onSortingChange: setSorting,
        onGlobalFilterChange: setGlobalFilter,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: {
            pagination: {
                pageSize: 10,
            },
        },
    });

    if (isLoading) {
        return (
            <div className="loading-overlay">
                <div className="loading-spinner"></div>
                <p>Verträge werden geladen...</p>
            </div>
        );
    }

    if (!data || data.length === 0) {
        return (
            <div className="empty-state">
                <FileText size={64} />
                <h3>Keine Verträge vorhanden</h3>
                <p>Erstellen Sie Ihren ersten Vertrag, um loszulegen.</p>
            </div>
        );
    }

    return (
        <>
            {/* Table Controls */}
            <div className="table-controls">
                <div className="table-controls-left">
                    <div className="search-box">
                        <svg className="search-icon" width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                        </svg>
                        <input
                            type="text"
                            className="search-input"
                            placeholder="Verträge durchsuchen..."
                            value={globalFilter ?? ''}
                            onChange={e => setGlobalFilter(e.target.value)}
                        />
                    </div>
                </div>
                <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary, #64748b)' }}>
                    {table.getFilteredRowModel().rows.length} Einträge
                </div>
            </div>

            {/* Table */}
            <div className="table-wrapper">
                <table>
                    <thead>
                        {table.getHeaderGroups().map(headerGroup => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map(header => (
                                    <th
                                        key={header.id}
                                        className={header.column.getCanSort() ? 'sortable' : ''}
                                        onClick={header.column.getToggleSortingHandler()}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                            {flexRender(header.column.columnDef.header, header.getContext())}
                                            {header.column.getCanSort() && (
                                                <span style={{ display: 'inline-flex', flexDirection: 'column', fontSize: '0.6rem' }}>
                                                    {header.column.getIsSorted() === 'asc' ? (
                                                        <ChevronUp size={14} />
                                                    ) : header.column.getIsSorted() === 'desc' ? (
                                                        <ChevronDown size={14} />
                                                    ) : (
                                                        <span style={{ opacity: 0.3 }}>
                                                            <ChevronUp size={14} style={{ marginBottom: '-6px' }} />
                                                            <ChevronDown size={14} />
                                                        </span>
                                                    )}
                                                </span>
                                            )}
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {table.getRowModel().rows.map(row => (
                            <tr key={row.id}>
                                {row.getVisibleCells().map(cell => (
                                    <td key={cell.id}>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {table.getPageCount() > 1 && (
                <div className="pagination">
                    <div className="pagination-info">
                        Zeige {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1}-
                        {Math.min(
                            (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
                            table.getFilteredRowModel().rows.length
                        )}{' '}
                        von {table.getFilteredRowModel().rows.length}
                    </div>
                    <div className="pagination-controls">
                        <ModernPagination
                            currentPage={table.getState().pagination.pageIndex}
                            totalPages={table.getPageCount()}
                            onPageChange={(pageIndex) => table.setPageIndex(pageIndex)}
                            canPreviousPage={table.getCanPreviousPage()}
                            canNextPage={table.getCanNextPage()}
                            onPreviousPage={() => table.previousPage()}
                            onNextPage={() => table.nextPage()}
                        />
                    </div>
                </div>
            )}
        </>
    );
};

export default ContractsTable;
